import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import spApi from '../../api/serviceProviders/spApi';
import { showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';

const Profile = () => {
  const [profile, setProfile] = useState({
    name: '',
    mobile: '',
    email: '',
    website: '',
    description: '',
    facebook: '',
    instagram: '',
    location: '',
    categories: [],
    wallpaper: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await spApi.getProfile();
      if (response.success || response.status === 800) {
        setProfile(response.data?.provider || response.data || {});
      } else {
        setError('Hitilafu kupata taarifa za profile');
      }
    } catch (err) {
      setError(err.message || 'Hitilafu kupata taarifa');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    // Clean payload: only include clean editable fields and map category objects to scalar ID integers
    const payload = {
      name: profile.name || '',
      mobile: profile.mobile || '',
      email: profile.email || '',
      website: profile.website || '',
      description: profile.description || '',
      facebook: profile.facebook || '',
      instagram: profile.instagram || '',
      location: profile.location || '',
    };

    if (profile.location_id) {
      payload.location_id = profile.location_id;
    }

    if (Array.isArray(profile.categories) && profile.categories.length > 0) {
      payload.categories = profile.categories
        .map(cat => (typeof cat === 'object' ? (cat.id || cat.category_id) : cat))
        .filter(id => id !== undefined && id !== null);
    }

    try {
      const response = await spApi.updateProfile(profile.id, payload);
      showApiMessage(response);
      if (response.success || response.status === 800) {
        toast.success('Taarifa zimehifadhiwa kikamilifu!');
      } else {
        toast.error(response.message || 'Hitilafu kuhifadhi taarifa');
      }
    } catch (err) {
      showApiMessage(err);
      toast.error(err.message || 'Kuna tatizo limejitokeza. Tafadhali jaribu tena.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center mt-5" style={{ minHeight: '300px' }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <h1 className="stylish-heading text-center" style={{ color: '#4169e1' }}>Profile</h1>
      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm border-0 mb-4 overflow-hidden">
        {/* Profile Header / Wallpaper */}
        <div style={{
          height: '250px',
          backgroundColor: '#f8f9fa',
          backgroundImage: profile.wallpaper?.lg_photo ? `url(${profile.wallpaper.lg_photo})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative'
        }}>
          {!profile.wallpaper?.lg_photo && (
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
              <span>Picha ya jalada haijawekwa</span>
            </div>
          )}
        </div>

        <Card.Body className="p-4 position-relative">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h3 className="mb-1">{profile.name || 'Jina la Biashara'}</h3>
              <p className="text-muted mb-2">
                <i className="bi bi-geo-alt-fill me-2 text-primary"></i>
                {profile.location || 'Mahali hakujatajwa'}
              </p>
              <div className="d-flex flex-wrap gap-2 mt-2">
                {profile.categories?.map((cat) => (
                  <span key={cat.id} className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2 rounded-pill">
                    {cat.category}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Vifurushi / Packages Section */}
          <div className="mt-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0" style={{ color: '#334155' }}>
                <i className="bi bi-box-seam me-2 text-primary"></i>
                Vifurushi vya Huduma (Packages)
              </h5>
            </div>

            {Array.isArray(profile.packages) && profile.packages.length > 0 ? (
              <Row className="g-3">
                {profile.packages.map((pkg, idx) => (
                  <Col key={pkg.id || idx} md={6} lg={4}>
                    <Card className="h-100 border shadow-sm rounded-3">
                      <Card.Body className="d-flex flex-column justify-content-between">
                        <div>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="fw-bold text-dark mb-0">{pkg.package_name || pkg.name || 'Kifurushi'}</h6>
                            <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1">
                              TZS {parseFloat(pkg.price || 0).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-muted small mb-0" style={{ fontSize: '0.85rem' }}>
                            {pkg.package_description || pkg.description || 'Bila maelezo'}
                          </p>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <Alert variant="light" className="border text-center text-muted py-3">
                Hakuna vifurushi vilivyowekwa bado.
              </Alert>
            )}
          </div>

          <hr />
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Jina la Biashara</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={profile.name || ''}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Namba ya Simu</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile"
                    value={profile.mobile || ''}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Barua Pepe (Email)</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={profile.email || ''}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mkoa / Mahali (Location)</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={profile.location || ''}
                    onChange={handleInputChange}
                    placeholder="Mfano: Arusha, Dar es salaam"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tovuti (Website)</Form.Label>
                  <Form.Control
                    type="text"
                    name="website"
                    value={profile.website || ''}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Maelezo (Description)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="description"
                    value={profile.description || ''}
                    onChange={handleInputChange}
                    placeholder="Eleza kwa ufupi huduma unazotoa na kwanini wateja wakuchague wewe..."
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Instagram Link</Form.Label>
                  <Form.Control
                    type="text"
                    name="instagram"
                    value={profile.instagram || ''}
                    onChange={handleInputChange}
                    placeholder="https://instagram.com/..."
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Facebook Link</Form.Label>
                  <Form.Control
                    type="text"
                    name="facebook"
                    value={profile.facebook || ''}
                    onChange={handleInputChange}
                    placeholder="https://facebook.com/..."
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-4">
              <Button variant="primary" type="submit" disabled={saving} className="px-4">
                {saving ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                Hifadhi Taarifa
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Profile;
